import React from 'react';
import Icon from '../../../components/AppIcon';

const AttributionModelingPanel = ({ attributionData, attributionModel }) => {
  const getModelDescription = (model) => {
    const descriptions = {
      first_touch: 'Gives 100% credit to the first touchpoint in the customer journey',
      last_touch: 'Gives 100% credit to the final touchpoint before conversion',
      linear: 'Distributes credit equally across all touchpoints',
      time_decay: 'Gives more credit to touchpoints closer to conversion',
      multi_touch: 'Distributes credit based on touchpoint importance and position'
    };
    return descriptions?.[model] || '';
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Icon name="GitBranch" className="w-5 h-5 text-blue-600" />
            Attribution Modeling
          </h2>
          <p className="text-sm text-gray-600 mt-1">{getModelDescription(attributionModel)}</p>
        </div>
      </div>
      {/* Customer Journey Touchpoints */}
      <div className="mb-6">
        <h3 className="text-sm font-medium text-gray-700 mb-4">Customer Journey Touchpoints</h3>
        <div className="relative">
          {/* Journey path line */}
          <div className="absolute top-6 left-8 right-8 h-0.5 bg-gray-300" />
          
          <div className="grid grid-cols-4 gap-4">
            {attributionData?.map((touchpoint, index) => (
              <div key={touchpoint?.id} className="relative">
                <div className="bg-white border-2 border-blue-600 rounded-lg p-4 text-center">
                  <div className="w-12 h-12 mx-auto bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-lg mb-2">
                    {touchpoint?.credit}%
                  </div>
                  <h4 className="font-medium text-gray-900 text-sm mb-1">{touchpoint?.touchpoint}</h4>
                  <p className="text-xs text-gray-500 mb-2">{touchpoint?.channel}</p>
                  <div className="text-xs text-gray-600">
                    <p>{touchpoint?.interactions} interactions</p>
                    {touchpoint?.avgTimeToNext !== 'N/A' && (
                      <p className="mt-1">→ {touchpoint?.avgTimeToNext}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Credit Distribution */}
      <div className="mb-6">
        <h3 className="text-sm font-medium text-gray-700 mb-4">Credit Distribution Across Channels</h3>
        <div className="space-y-3">
          {attributionData?.map((touchpoint) => (
            <div key={touchpoint?.id}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-gray-700">{touchpoint?.channel}</span>
                <span className="text-sm font-bold text-blue-600">{touchpoint?.credit}%</span>
              </div>
              <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-500"
                  style={{ width: `${touchpoint?.credit}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Multi-Touch Attribution Insights */}
      <div className="bg-blue-50 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="Info" className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Attribution Insights</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Average customer journey involves {attributionData?.length} touchpoints</li>
              <li>• Evaluation stage shows highest credit allocation at {attributionData?.find(t => t?.touchpoint === 'Evaluation')?.credit}%</li>
              <li>• Average time from awareness to conversion: {attributionData?.reduce((sum, t) => {
                const days = parseFloat(t?.avgTimeToNext?.split(' ')?.[0]) || 0;
                return sum + days;
              }, 0)?.toFixed(1)} days</li>
              <li>• Multi-touch model provides most accurate attribution for complex B2B sales cycles</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttributionModelingPanel;